import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const GASTOS_CATS = ['GMF', '4X1000', 'COMISION', 'CUOTA MANEJO', 'INTERESES', 'IVA', 'COBRO BANCARIO', 'GASTO FINANCIERO', 'RETENCION'];

const TIPO_ORDER = ['consig_no_libros', 'nd_no_libros', 'consig_no_extracto', 'cheques_pendientes'];
const TIPO_LABELS = {
  consig_no_libros:   'Consig. / NC no en libros',
  nd_no_libros:       'ND / Gastos no en libros',
  consig_no_extracto: 'Consig. / NC no en extracto',
  cheques_pendientes: 'Cheques / Créditos pendientes',
};

const getBancoPanelItems = (bancoData) => {
  const gastos = {};
  const consignaciones = [];
  const nds = [];
  (bancoData || []).forEach((r, i) => {
    if (r._cruza) return;
    if (r._gasto_bancario) {
      const det = (r['DETALLE'] || '').toString().toUpperCase();
      const cat = GASTOS_CATS.find(c => det.includes(c)) || 'GASTO BANCARIO';
      if (!gastos[cat]) gastos[cat] = { indices: [], total: 0 };
      gastos[cat].indices.push(i);
      gastos[cat].total += Math.abs(parseFloat((r['VALOR'] || '0').toString().replace(/[^0-9.-]/g, '')) || 0);
    } else {
      const val = parseFloat((r['VALOR'] || '0').toString().replace(/[^0-9.-]/g, '')) || 0;
      const item = { _origIdx: i, _isGroup: false, ...r };
      if (val >= 0) consignaciones.push(item);
      else nds.push(item);
    }
  });
  const gruposGastos = Object.entries(gastos).map(([cat, g]) => ({
    _isGroup: true, _groupKey: `grp_${cat}`, _groupIndices: g.indices,
    _groupCat: cat, _groupTotal: g.total, _groupCount: g.indices.length
  }));
  const result = [];
  if (consignaciones.length > 0) {
    result.push({ _isSection: true, _sectionKey: 'sec_consig', label: TIPO_LABELS.consig_no_libros });
    result.push(...consignaciones);
  }
  const ndAll = [...nds, ...gruposGastos];
  if (ndAll.length > 0) {
    result.push({ _isSection: true, _sectionKey: 'sec_nd', label: TIPO_LABELS.nd_no_libros });
    result.push(...ndAll);
  }
  return result;
};

const auxGetCategory = (r, headers, cfgObj) => {
  const hD = (headers || [])[(parseInt(cfgObj.col_debito_auxiliar || '7') - 1)];
  const hC = (headers || [])[(parseInt(cfgObj.col_credito_auxiliar || '8') - 1)];
  const d = hD ? parseFloat((r[hD] || '0').toString().replace(/[^0-9.-]/g, '')) || 0 : 0;
  const c = hC ? parseFloat((r[hC] || '0').toString().replace(/[^0-9.-]/g, '')) || 0 : 0;
  return (c > 0 && c >= d) ? 'cheques_pendientes' : 'consig_no_extracto';
};

const auxGetFecha = (r, headers, cfgObj) => {
  const hF = (headers || [])[(parseInt(cfgObj.col_fecha_auxiliar || '1') - 1)];
  return hF ? (r[hF] || '').toString().substring(0, 10) : '';
};

const auxGetVal = (r, headers, cfgObj) => {
  const iD = parseInt(cfgObj.col_debito_auxiliar || '7') - 1;
  const iC = parseInt(cfgObj.col_credito_auxiliar || '8') - 1;
  const hD = (headers || [])[iD]; const hC = (headers || [])[iC];
  const d = hD ? (r[hD] || '').toString().trim() : '';
  const c = hC ? (r[hC] || '').toString().trim() : '';
  if (d && d !== '0' && d !== '0.0' && !isNaN(parseFloat(d)))
    return `D: $${parseFloat(d).toLocaleString('es-CO', { minimumFractionDigits: 0 })}`;
  if (c && c !== '0' && c !== '0.0' && !isNaN(parseFloat(c)))
    return `C: $${parseFloat(c).toLocaleString('es-CO', { minimumFractionDigits: 0 })}`;
  return '';
};

const auxGetDesc = (r, headers, cfgObj) => {
  const skip = new Set([
    parseInt(cfgObj.col_fecha_auxiliar || '1') - 1,
    parseInt(cfgObj.col_debito_auxiliar || '7') - 1,
    parseInt(cfgObj.col_credito_auxiliar || '8') - 1,
  ]);
  return (headers || [])
    .map((h, idx) => !skip.has(idx) && h && h !== 'nan' ? (r[h] || '') : null)
    .filter(v => v && v.toString().trim() && v.toString() !== '0')
    .slice(0, 2)
    .join(' · ')
    .substring(0, 50);
};

export default function Dashboard() {
  const { user, logout, API, suscripcion, suscripcionActiva, diasRestantes } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState({
    hoja_extracto: 'DEBITOS', hoja_auxiliar: 'DEBITOS',
    col_valor_extracto: '6', col_valor_auxiliar: '7',
    col_fecha_extracto: '4', col_concepto_extracto: '7',
    col_fecha_auxiliar: '1', col_debito_auxiliar: '7', col_credito_auxiliar: '8',
    extracto_dos_columnas: false, col_debito_extracto: '6', col_credito_extracto: '7',
    tolerancia_dias: '0', nombre: 'Conciliación',
    empresa: 'COMBURED SAS', nit: '900.675.169-7',
    banco: 'BANCOLOMBIA', tipo_cuenta: 'CORRIENTE',
    numero_cuenta: '', codigo: '', mes: '',
    responsable: 'VALENTINA JARAMILLO GAVIRIA', cargo: 'AUXILIAR CONTABLE',
    saldo_extracto: '', saldo_libros: ''
  });
  const [files, setFiles] = useState({ extracto: null, auxiliar: null });
  const [resultado, setResultado] = useState(null);
  const [tab, setTab] = useState('banco');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [convirtiendoPdf, setConvirtiendoPdf] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState('');
  const [showPartidas, setShowPartidas] = useState(false);
  const [partidas, setPartidas] = useState([]);
  const [nuevaPartida, setNuevaPartida] = useState({ tipo: 'consig_no_libros', fecha: '', documento: '', descripcion: '', valor: '' });
  const [guardandoPartidas, setGuardandoPartidas] = useState(false);
  const [mesOrigen, setMesOrigen] = useState('');
  const [trasladandoMsg, setTrasladandoMsg] = useState('');
  const [modoCreuce, setModoCreuce] = useState(false);
  const [selBancoIdxs, setSelBancoIdxs] = useState([]);
  const [selAuxIdxs, setSelAuxIdxs] = useState([]);
  const [crucesManuales, setCrucesManuales] = useState([]);

  const extractoEsPDF = !!(files.extracto?.name?.toLowerCase().endsWith('.pdf'));

  // ── Cuentas bancarias ────────────────────────────────────────
  const [cuentas, setCuentas] = useState([]);
  const [cuentaId, setCuentaId] = useState('');

  useState(() => {
    axios.get(`${API}/cuentas`).then(r => setCuentas(r.data)).catch(() => {});
  }, []);

  const cargarCuenta = (id) => {
    setCuentaId(id);
    if (!id) return;
    const c = cuentas.find(c => String(c.id) === String(id));
    if (!c) return;
    setCfg(prev => ({
      ...prev,
      empresa: c.empresa || prev.empresa,
      nit: c.nit || prev.nit,
      banco: c.banco || prev.banco,
      tipo_cuenta: c.tipo_cuenta || prev.tipo_cuenta,
      numero_cuenta: c.numero_cuenta || prev.numero_cuenta,
      codigo: c.codigo || prev.codigo,
      responsable: c.responsable || prev.responsable,
      cargo: c.cargo || prev.cargo,
      ...(c.config || {}),
    }));
  };

  // ── Plantillas ──────────────────────────────────────────────
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaId, setPlantillaId] = useState('');
  const [guardandoPlantilla, setGuardandoPlantilla] = useState(false);
  const [nombrePlantilla, setNombrePlantilla] = useState('');

  useState(() => {
    axios.get(`${API}/plantillas`).then(r => setPlantillas(r.data)).catch(() => {});
  }, []);

  const cargarPlantilla = (id) => {
    setPlantillaId(id);
    if (!id) return;
    const p = plantillas.find(p => String(p.id) === String(id));
    if (p) setCfg(prev => ({ ...prev, ...p.config }));
  };

  const guardarPlantilla = async () => {
    if (!nombrePlantilla.trim()) return;
    const tplCfg = {
      banco: cfg.banco,
      hoja_extracto: cfg.hoja_extracto, col_fecha_extracto: cfg.col_fecha_extracto,
      col_valor_extracto: cfg.col_valor_extracto, col_concepto_extracto: cfg.col_concepto_extracto,
      extracto_dos_columnas: cfg.extracto_dos_columnas,
      col_debito_extracto: cfg.col_debito_extracto, col_credito_extracto: cfg.col_credito_extracto,
      hoja_auxiliar: cfg.hoja_auxiliar, col_fecha_auxiliar: cfg.col_fecha_auxiliar,
      col_debito_auxiliar: cfg.col_debito_auxiliar, col_credito_auxiliar: cfg.col_credito_auxiliar,
      tolerancia_dias: cfg.tolerancia_dias,
    };
    const fd = new FormData();
    fd.append('nombre', nombrePlantilla);
    fd.append('banco', cfg.banco);
    fd.append('config_json', JSON.stringify(tplCfg));
    await axios.post(`${API}/plantillas`, fd);
    const r = await axios.get(`${API}/plantillas`);
    setPlantillas(r.data);
    setGuardandoPlantilla(false);
    setNombrePlantilla('');
  };

  const eliminarPlantilla = async (id) => {
    await axios.delete(`${API}/plantillas/${id}`);
    setPlantillas(prev => prev.filter(p => p.id !== id));
    if (String(plantillaId) === String(id)) setPlantillaId('');
  };

  const handle = e => setCfg({ ...cfg, [e.target.name]: e.target.value });
  const setFile = (key, file) => setFiles({ ...files, [key]: file });

  const cuentaLabel = cfg.numero_cuenta || (cfg.banco ? `${cfg.banco}${cfg.tipo_cuenta ? ' - ' + cfg.tipo_cuenta : ''}` : '');

  const calcMesAnterior = (mes) => {
    const MESES = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
    const mAno = mes.match(/^(\d{4})-(\d{2})$/);
    if (mAno) {
      const d = new Date(parseInt(mAno[1]), parseInt(mAno[2]) - 2, 1);
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    }
    const mNombre = mes.toLowerCase().match(/^(\w+)\s+(\d{4})$/);
    if (mNombre) {
      const idx = MESES.findIndex(m => mNombre[1].startsWith(m.substring(0,3)));
      if (idx >= 0) return idx === 0 ? `diciembre ${parseInt(mNombre[2])-1}` : `${MESES[idx-1]} ${mNombre[2]}`;
    }
    return '';
  };

  const guardarPartidasDB = async () => {
    if (!partidas.length || !cfg.mes || !cuentaLabel) return;
    setGuardandoPartidas(true);
    try {
      const fd = new FormData();
      fd.append('cuenta', cuentaLabel);
      fd.append('mes', cfg.mes);
      if (resultado?.conciliacion_id) fd.append('conciliacion_id', resultado.conciliacion_id);
      fd.append('partidas_json', JSON.stringify(partidas));
      await axios.post(`${API}/partidas/guardar-lote`, fd);
      setTrasladandoMsg(`✅ ${partidas.length} partidas guardadas para "${cfg.mes}"`);
    } catch (e) {
      setTrasladandoMsg('❌ ' + (e.response?.data?.detail || 'Error al guardar'));
    } finally {
      setGuardandoPartidas(false);
    }
  };

  const trasladarPartidas = async () => {
    if (!mesOrigen.trim() || !cfg.mes || !cuentaLabel) return;
    setTrasladandoMsg('');
    try {
      const fd = new FormData();
      fd.append('cuenta', cuentaLabel);
      fd.append('mes_origen', mesOrigen.trim());
      fd.append('mes_destino', cfg.mes);
      const r = await axios.post(`${API}/partidas/trasladar`, fd);
      setPartidas(prev => [...prev, ...r.data.partidas]);
      setTrasladandoMsg(`✅ ${r.data.trasladadas} partidas cargadas de "${mesOrigen.trim()}"`);
      setMesOrigen('');
    } catch (e) {
      setTrasladandoMsg('❌ ' + (e.response?.data?.detail || 'Error al trasladar'));
    }
  };

  const tiposPartida = {
    'consig_no_libros': 'Consig./Notas crédito no en libros',
    'nd_no_libros': 'Notas débito/Cheques no en libros',
    'consig_no_extracto': 'Consig. no en extracto',
    'cheques_pendientes': 'Cheques pendientes de cobro'
  };

  const cruzarManual = () => {
    if (selBancoIdxs.length === 0 || selAuxIdxs.length === 0) return;
    const items = getBancoPanelItems(resultado?.banco || []);

    // Un ítem del banco puede ser un grupo de gastos (varias filas reales
    // detrás de un solo total, ej. 4 cobros de 4x1000) — se expande a todos
    // sus índices reales. Varios ítems del banco seleccionados a la vez
    // también se expanden y se juntan (permite cruzar N filas del banco
    // contra M del auxiliar, ej. dos de $500 en el auxiliar contra un $1.000
    // del extracto).
    const bancoIndices = selBancoIdxs.flatMap(key => {
      const item = items.find(p => !p._isSection && (p._isGroup ? p._groupKey === key : p._origIdx === key));
      if (!item) return [];
      return item._isGroup ? item._groupIndices : [item._origIdx];
    });

    const auxIndices = selAuxIdxs.filter(k => !(typeof k === 'string' && k.startsWith('part_')));
    const partidaIndices = selAuxIdxs
      .filter(k => typeof k === 'string' && k.startsWith('part_'))
      .map(k => parseInt(k.replace('part_', '')));

    setResultado(prev => {
      const banco = prev.banco.map((r, i) => bancoIndices.includes(i) ? { ...r, _cruza: true } : r);
      const auxiliar = prev.auxiliar.map((r, i) => auxIndices.includes(i) ? { ...r, _cruza: true } : r);
      const cruzan = banco.filter(r => r._cruza).length;
      return {
        ...prev, banco, auxiliar, cruzan,
        no_cruzan_banco: banco.filter(r => !r._cruza).length,
        no_cruzan_aux: auxiliar.filter(r => !r._cruza).length,
        porcentaje_conciliacion: banco.length > 0 ? Math.round(cruzan / banco.length * 1000) / 10 : 0
      };
    });
    if (partidaIndices.length > 0) {
      setPartidas(prev => prev.filter((_, i) => !partidaIndices.includes(i)));
    }
    // Cada índice queda registrado en al menos un registro de cruce manual
    // (el backend solo mira, por registro, el banco_idx y/o aux_idx presentes
    // — no hace falta preservar qué se cruzó específicamente con qué).
    setCrucesManuales(prev => [
      ...prev,
      ...bancoIndices.map(bi => ({ banco_idx: bi })),
      ...auxIndices.map(ai => ({ aux_idx: ai })),
    ]);
    setSelBancoIdxs([]);
    setSelAuxIdxs([]);
  };

  const toggleSelBanco = (key) => setSelBancoIdxs(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  const toggleSelAux = (key) => setSelAuxIdxs(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  // Totales de lo seleccionado en cada lado, para que el usuario pueda
  // verificar que cuadran (ej. 2 de $500 en auxiliar = 1 de $1.000 en banco)
  // antes de confirmar el cruce.
  const totalSeleccionBanco = () => {
    const items = getBancoPanelItems(resultado?.banco || []);
    return selBancoIdxs.reduce((acc, key) => {
      const item = items.find(p => !p._isSection && (p._isGroup ? p._groupKey === key : p._origIdx === key));
      if (!item) return acc;
      if (item._isGroup) return acc + item._groupTotal;
      return acc + Math.abs(parseFloat((item['VALOR'] || '0').toString().replace(/[^0-9.-]/g, '')) || 0);
    }, 0);
  };
  const totalSeleccionAux = () => {
    return selAuxIdxs.reduce((acc, key) => {
      if (typeof key === 'string' && key.startsWith('part_')) {
        const p = partidas[parseInt(key.replace('part_', ''))];
        return acc + (p ? Math.abs(parseFloat(p.valor) || 0) : 0);
      }
      const r = resultado?.auxiliar?.[key];
      if (!r) return acc;
      const val = auxGetVal(r, resultado.headers_aux, cfg);
      return acc + Math.abs(parseFloat((val || '0').toString().replace(/[^0-9.-]/g, '')) || 0);
    }, 0);
  };

  const conciliar = async () => {
    if (!files.extracto || !files.auxiliar) { setError('Sube los dos archivos'); return; }
    setError(''); setLoading(true); setResultado(null);
    setCrucesManuales([]); setModoCreuce(false); setSelBancoIdxs([]); setSelAuxIdxs([]);
    try {
      const fd = new FormData();
      fd.append('extracto', files.extracto);
      fd.append('auxiliar', files.auxiliar);
      Object.entries(cfg).forEach(([k, v]) => fd.append(k, v));
      const r = await axios.post(`${API}/conciliar`, fd);
      setResultado(r.data);
    } catch (e) {
      if (e.response?.status === 402) {
        nav('/suscripcion');
        return;
      }
      setError(e.response?.data?.detail || 'Error al conciliar');
    } finally {
      setLoading(false);
    }
  };

  const convertirPdfAExcel = async () => {
    if (!files.extracto) return;
    setConvirtiendoPdf(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('extracto', files.extracto);
      const r = await axios.post(`${API}/pdf-a-excel`, fd, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const nombreBase = files.extracto.name.replace(/\.pdf$/i, '');
      const a = document.createElement('a'); a.href = url; a.download = `${nombreBase}.xlsx`; a.click();
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        const texto = await e.response.data.text();
        try { setError(JSON.parse(texto).detail || 'No se pudo convertir el PDF'); }
        catch { setError('No se pudo convertir el PDF'); }
      } else {
        setError(e.response?.data?.detail || 'No se pudo convertir el PDF');
      }
    } finally {
      setConvirtiendoPdf(false);
    }
  };

  const exportar = async () => {
    if (!files.extracto || !files.auxiliar) return;
    setExporting(true);
    try {
      const fd = new FormData();
      fd.append('extracto', files.extracto);
      fd.append('auxiliar', files.auxiliar);
      Object.entries(cfg).forEach(([k, v]) => { if (k !== 'nombre') fd.append(k, v); });
      const r = await axios.post(`${API}/exportar`, fd, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = 'conciliacion.xlsx'; a.click();
    } catch (e) { setError('Error al exportar'); }
    finally { setExporting(false); }
  };

  const generarReporte = async () => {
    if (!files.extracto || !files.auxiliar) return;
    setGenerando(true);
    try {
      const fd = new FormData();
      fd.append('extracto', files.extracto);
      fd.append('auxiliar', files.auxiliar);
      Object.entries(cfg).forEach(([k, v]) => { if (k !== 'nombre') fd.append(k, v); });
      fd.append('partidas_json', JSON.stringify(partidas));
      fd.append('cruces_manuales_json', JSON.stringify(crucesManuales));
      const r = await axios.post(`${API}/reporte`, fd, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Conciliacion_${cfg.banco}_${cfg.mes}.xlsx`;
      a.click();
      // Sincronizar partidas con la BD para que el historial descargue igual
      if (resultado?.conciliacion_id) {
        try {
          const fdSync = new FormData();
          fdSync.append('partidas_json', JSON.stringify(partidas));
          fdSync.append('cruces_manuales_json', JSON.stringify(crucesManuales));
          await axios.post(`${API}/conciliaciones/${resultado.conciliacion_id}/sync-partidas`, fdSync);
        } catch {} // no crítico
      }
    } catch (e) {
      if (e.response?.status === 402) {
        nav('/suscripcion');
        return;
      }
      setError('Error al generar reporte');
    }
    finally { setGenerando(false); }
  };

  const agregarPartida = () => {
    if (!nuevaPartida.descripcion || !nuevaPartida.valor) return;
    setPartidas([...partidas, { ...nuevaPartida, valor: parseFloat(nuevaPartida.valor) }]);
    setNuevaPartida({ tipo: 'consig_no_libros', fecha: '', documento: '', descripcion: '', valor: '' });
  };

  const eliminarPartida = (i) => setPartidas(partidas.filter((_, idx) => idx !== i));

  const calcularSumas = () => {
    const se = parseFloat(cfg.saldo_extracto) || 0;
    const sl = parseFloat(cfg.saldo_libros) || 0;
    const consig_nl = partidas.filter(p => p.tipo === 'consig_no_libros').reduce((a, p) => a + p.valor, 0);
    const nd_nl = partidas.filter(p => p.tipo === 'nd_no_libros').reduce((a, p) => a + p.valor, 0);
    const consig_ne = partidas.filter(p => p.tipo === 'consig_no_extracto').reduce((a, p) => a + p.valor, 0);
    const cheques = partidas.filter(p => p.tipo === 'cheques_pendientes').reduce((a, p) => a + p.valor, 0);
    const banco_aj = se + consig_nl - nd_nl - consig_ne + cheques;
    const aux_aj = sl + consig_nl - nd_nl - consig_ne + cheques;
    return { banco_aj, aux_aj, diff: banco_aj - aux_aj };
  };

  const rows = resultado ? (
    tab === 'banco' ? resultado.banco :
    tab === 'auxiliar' ? resultado.auxiliar :
    [...resultado.banco.filter(r => !r._cruza), ...resultado.auxiliar.filter(r => !r._cruza)]
  ) : [];

  const sumas = calcularSumas();
  const fmt = n => Number(n).toLocaleString('es-CO', { minimumFractionDigits: 2 });
  const bancoPanelItems = resultado ? getBancoPanelItems(resultado.banco) : [];
  const auxSorted = resultado ? (() => {
    const consig = [], cheques = [];
    resultado.auxiliar.forEach((r, i) => {
      if (r._cruza) return;
      if (auxGetCategory(r, resultado.headers_aux, cfg) === 'cheques_pendientes') cheques.push({ r, i });
      else consig.push({ r, i });
    });
    return { consig, cheques };
  })() : { consig: [], cheques: [] };
  const sHdr = { padding: '2px 7px', fontSize: 10, fontWeight: 700, color: '#fff', background: '#374151', borderRadius: 2, marginTop: 5, display: 'block' };

  const esAuditor   = user?.rol === 'auditor';
  const esDigitador = user?.rol === 'digitador';

  // Auditor: redirigir al historial (no puede conciliar)
  if (esAuditor) return (
    <div style={s.page}>
      <nav style={s.nav}>
        <span style={s.navBrand}>🏦 ConciliaApp</span>
        <div style={s.navRight}>
          <span style={s.navUser}>👤 {user?.nombre} · <em>auditor</em></span>
          <a href="/kpis" style={s.navLink}>📊 KPIs</a>
          <a href="/historial" style={s.navLink}>📋 Historial</a>
          <button onClick={logout} style={s.navBtn}>Salir</button>
        </div>
      </nav>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '70vh', gap: 16 }}>
        <div style={{ fontSize: 48 }}>🔍</div>
        <h2 style={{ margin: 0, color: '#333' }}>Modo auditor</h2>
        <p style={{ color: '#666', textAlign: 'center', maxWidth: 360 }}>
          Tu rol es de solo lectura. Puedes consultar el historial de conciliaciones y descargar reportes, pero no crear nuevas conciliaciones.
        </p>
        <a href="/historial" style={{ padding: '10px 24px', background: '#2563eb', color: '#fff', borderRadius: 9, textDecoration: 'none', fontWeight: 600 }}>Ver historial</a>
        <a href="/kpis" style={{ color: '#2563eb', fontSize: 13 }}>Ver KPIs →</a>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <span style={s.navBrand}>🏦 ConciliaApp</span>
        <div style={s.navRight}>
          <span style={s.navUser}>👤 {user?.nombre}</span>
          {user?.rol === 'admin' && <a href="/admin" style={s.navLink}>Admin</a>}
          <a href="/kpis" style={s.navLink}>📊 KPIs</a>
          <a href="/cuentas" style={s.navLink}>🏦 Cuentas</a>
          <a href="/calendario-tributario" style={s.navLink}>📅 Calendario</a>
          <a href="/historial" style={s.navLink}>📋 Historial</a>
          <a href="/retencion-fuente" style={s.navLink}>🧾 Retención</a>
          <a href="/suscripcion" style={s.navLink}>💳 Plan</a>
          <button onClick={logout} style={s.navBtn}>Salir</button>
        </div>
      </nav>

      {/* Banner de suscripción */}
      {suscripcion && suscripcion.estado === 'trial' && diasRestantes !== null && diasRestantes <= 7 && (
        <div style={{ background: '#fffbeb', borderBottom: '1px solid #fbbf24', padding: '8px 24px', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Tu periodo de prueba vence en <strong>{diasRestantes} día{diasRestantes !== 1 ? 's' : ''}</strong>. Suscríbete para continuar.</span>
          <a href="/suscripcion" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>Ver planes →</a>
        </div>
      )}
      {suscripcion && suscripcion.estado === 'vencida' && (
        <div style={{ background: '#fee2e2', borderBottom: '1px solid #fca5a5', padding: '8px 24px', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Tu suscripción ha <strong>vencido</strong>. Las conciliaciones nuevas están bloqueadas.</span>
          <a href="/suscripcion" style={{ color: '#dc2626', fontWeight: 600, textDecoration: 'none' }}>Renovar ahora →</a>
        </div>
      )}

      <div style={s.content}>
        <div style={s.grid}>
          {/* Panel izquierdo */}
          <div style={s.panel}>
            <h2 style={s.panelTitle}>Nueva conciliación</h2>

            <div style={s.field}>
              <label style={s.label}>Nombre</label>
              <input style={s.input} name="nombre" value={cfg.nombre} onChange={handle} />
            </div>

            {/* Selector de cuenta bancaria */}
            {cuentas.length > 0 && (
              <div style={s.field}>
                <label style={s.label}>Cuenta bancaria guardada</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select style={{ ...s.input, flex: 1 }} value={cuentaId}
                    onChange={e => cargarCuenta(e.target.value)}>
                    <option value="">— Seleccionar cuenta —</option>
                    {cuentas.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre} · {c.banco}</option>
                    ))}
                  </select>
                  <a href="/cuentas" style={s.btnManage} title="Gestionar cuentas">⚙️</a>
                </div>
              </div>
            )}
            {cuentas.length === 0 && (
              <div style={s.cuentaEmpty}>
                <span>No tienes cuentas guardadas.</span>
                <a href="/cuentas" style={{ color: '#2563eb', fontSize: 12 }}>Crear cuenta →</a>
              </div>
            )}

            {/* Selector de plantilla */}
            <div style={s.field}>
              <label style={s.label}>Plantilla de banco</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select style={{ ...s.input, flex: 1 }} value={plantillaId}
                  onChange={e => cargarPlantilla(e.target.value)}>
                  <option value="">— Seleccionar plantilla —</option>
                  {plantillas.filter(p => !p.es_mia).length > 0 && (
                    <optgroup label="Plantillas del sistema">
                      {plantillas.filter(p => !p.es_mia).map(p => (
                        <option key={p.id} value={p.id}>{p.nombre}</option>
                      ))}
                    </optgroup>
                  )}
                  {plantillas.filter(p => p.es_mia).length > 0 && (
                    <optgroup label="Mis plantillas">
                      {plantillas.filter(p => p.es_mia).map(p => (
                        <option key={p.id} value={p.id}>{p.nombre}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <button style={s.btnSaveTpl} title="Guardar configuración actual como plantilla"
                  onClick={() => setGuardandoPlantilla(v => !v)}>💾</button>
              </div>
              {plantillaId && plantillas.find(p => p.es_mia && String(p.id) === String(plantillaId)) && (
                <button style={s.btnDelTpl} onClick={() => eliminarPlantilla(plantillaId)}>
                  ✕ Eliminar esta plantilla
                </button>
              )}
            </div>
            {guardandoPlantilla && (
              <div style={s.tplSaveBox}>
                <input style={s.input} placeholder="Nombre de la plantilla*"
                  value={nombrePlantilla} onChange={e => setNombrePlantilla(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && guardarPlantilla()} />
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button style={s.btnAgregar} onClick={guardarPlantilla}>Guardar</button>
                  <button style={{ ...s.navBtn, fontSize: 13 }}
                    onClick={() => { setGuardandoPlantilla(false); setNombrePlantilla(''); }}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            <DropZone label="Extracto bancario" icon="🏦" accept=".xlsx,.xls,.pdf" file={files.extracto} onChange={f => setFile('extracto', f)} />
            {extractoEsPDF && (
              <div style={s.pdfNote}>
                📄 PDF detectado — fecha, valor y concepto se auto-detectan.
                {' '}Si no concilia bien,{' '}
                <button
                  type="button"
                  onClick={convertirPdfAExcel}
                  disabled={convirtiendoPdf}
                  style={s.pdfConvertLink}
                >
                  {convirtiendoPdf ? 'convirtiendo...' : 'conviértelo a Excel'}
                </button>
                {' '}y súbelo como extracto para ajustar las columnas a mano.
              </div>
            )}
            <DropZone label="Auxiliar contable" icon="📒" accept=".xlsx,.xls" file={files.auxiliar} onChange={f => setFile('auxiliar', f)} />

            {/* Saldos */}
            <div style={s.saldosBox}>
              <div style={s.saldosTitle}>💰 Saldos</div>
              <div style={s.advGrid}>
                <div style={s.field}>
                  <label style={s.label}>Saldo extracto banco</label>
                  <input style={s.input} name="saldo_extracto" value={cfg.saldo_extracto} onChange={handle} placeholder="0" type="number" />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Saldo según libros</label>
                  <input style={s.input} name="saldo_libros" value={cfg.saldo_libros} onChange={handle} placeholder="0" type="number" />
                </div>
              </div>
              {(cfg.saldo_extracto || cfg.saldo_libros) && (
                <div style={s.sumasBox}>
                  <div style={s.sumaRow}><span>Saldo ajustado banco:</span><span style={s.sumaVal}>${fmt(sumas.banco_aj)}</span></div>
                  <div style={s.sumaRow}><span>Saldo ajustado libros:</span><span style={s.sumaVal}>${fmt(sumas.aux_aj)}</span></div>
                  <div style={{...s.sumaRow, fontWeight:700, color: Math.abs(sumas.diff) < 1 ? '#16a34a' : '#dc2626'}}>
                    <span>Diferencia:</span><span>${fmt(sumas.diff)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Datos del reporte */}
            <details style={s.details}>
              <summary style={s.summary}>📋 Datos del reporte</summary>
              <div style={s.advGrid}>
                {[
                  ['empresa', 'Empresa'], ['nit', 'NIT'], ['banco', 'Banco'],
                  ['tipo_cuenta', 'Tipo cuenta'], ['numero_cuenta', 'Número cuenta'],
                  ['codigo', 'Código'], ['mes', 'Mes'], ['responsable', 'Responsable'], ['cargo', 'Cargo']
                ].map(([k, lbl]) => (
                  <div key={k} style={s.field}>
                    <label style={s.label}>{lbl}</label>
                    <input style={s.input} name={k} value={cfg[k]} onChange={handle} />
                  </div>
                ))}
              </div>
            </details>

            {/* Partidas conciliatorias — solo usuario y admin */}
            {esDigitador && (
              <div style={s.rolNote}>
                ✏️ Modo digitador: las partidas manuales y el reporte formal requieren rol <b>usuario</b>.
              </div>
            )}
            <details style={s.details} open={showPartidas && !esDigitador} hidden={esDigitador}>
              <summary style={s.summary} onClick={() => setShowPartidas(!showPartidas)}>📝 Partidas conciliatorias ({partidas.length})</summary>
              <div style={{ marginTop: 10 }}>
                {partidas.length > 0 && (
                  <div style={s.partidasList}>
                    {partidas.map((p, i) => (
                      <div key={i} style={s.partidaItem}>
                        <div style={s.partidaInfo}>
                          <span style={s.partidaTipo}>{tiposPartida[p.tipo]}</span>
                          <span style={s.partidaDesc}>{p.descripcion}</span>
                          <span style={s.partidaVal}>${fmt(p.valor)}</span>
                        </div>
                        <button onClick={() => eliminarPartida(i)} style={s.btnDel}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={s.nuevaPartida}>
                  <select style={s.input} value={nuevaPartida.tipo} onChange={e => setNuevaPartida({ ...nuevaPartida, tipo: e.target.value })}>
                    {Object.entries(tiposPartida).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <input style={s.input} placeholder="Fecha (dd/mm/aaaa)" value={nuevaPartida.fecha} onChange={e => setNuevaPartida({ ...nuevaPartida, fecha: e.target.value })} />
                  <input style={s.input} placeholder="Documento" value={nuevaPartida.documento} onChange={e => setNuevaPartida({ ...nuevaPartida, documento: e.target.value })} />
                  <input style={s.input} placeholder="Descripción*" value={nuevaPartida.descripcion} onChange={e => setNuevaPartida({ ...nuevaPartida, descripcion: e.target.value })} />
                  <input style={s.input} placeholder="Valor*" type="number" value={nuevaPartida.valor} onChange={e => setNuevaPartida({ ...nuevaPartida, valor: e.target.value })} />
                  <button style={s.btnAgregar} onClick={agregarPartida}>+ Agregar</button>
                </div>

                {/* ── Traslado entre meses ── */}
                <div style={s.trasladoBox}>
                  <div style={s.trasladoTitle}>📅 Trasladar del mes anterior</div>
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>
                    Carga en esta sesión las partidas pendientes que guardaste de un mes anterior.
                    {cuentaLabel && <span> Cuenta: <b>{cuentaLabel}</b></span>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      style={{ ...s.input, flex: 1 }}
                      placeholder={`Mes origen (ej: ${calcMesAnterior(cfg.mes) || 'enero 2025'})`}
                      value={mesOrigen}
                      onChange={e => { setMesOrigen(e.target.value); setTrasladandoMsg(''); }}
                    />
                    <button
                      style={{ ...s.btnAgregar, whiteSpace: 'nowrap' }}
                      onClick={trasladarPartidas}
                      disabled={!mesOrigen.trim() || !cfg.mes || !cuentaLabel}
                    >
                      📥 Trasladar
                    </button>
                  </div>
                  {!cuentaLabel && <p style={s.trasladoWarn}>⚠️ Completa los datos de banco / número de cuenta para habilitar el traslado.</p>}
                  {!cfg.mes && <p style={s.trasladoWarn}>⚠️ Indica el mes de la conciliación actual.</p>}
                  {trasladandoMsg && <p style={{ fontSize: 12, marginTop: 6, color: trasladandoMsg.startsWith('✅') ? '#16a34a' : '#dc2626' }}>{trasladandoMsg}</p>}
                </div>

                {/* ── Guardar partidas en BD ── */}
                {partidas.length > 0 && cfg.mes && cuentaLabel && (
                  <button style={s.btnGuardarPartidas} onClick={guardarPartidasDB} disabled={guardandoPartidas}>
                    {guardandoPartidas ? 'Guardando...' : `💾 Guardar ${partidas.length} partida${partidas.length > 1 ? 's' : ''} para el próximo mes`}
                  </button>
                )}
              </div>
            </details>

            {/* Config avanzada */}
            <details style={s.details}>
              <summary style={s.summary}>⚙️ Configuración avanzada</summary>
              {extractoEsPDF && (
                <p style={s.pdfNoteSmall}>Los campos del extracto no aplican para PDF (columnas auto-detectadas).</p>
              )}
              {!extractoEsPDF && (
                <label style={s.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={!!cfg.extracto_dos_columnas}
                    onChange={e => setCfg({ ...cfg, extracto_dos_columnas: e.target.checked })}
                  />
                  El extracto trae entradas y salidas en columnas separadas (débito/crédito)
                </label>
              )}
              <div style={s.advGrid}>
                {[
                  ['hoja_extracto', 'Hoja extracto', true],
                  ['hoja_auxiliar', 'Hoja auxiliar', false],
                  ['col_fecha_extracto', 'Col. fecha extracto', true],
                  ...(cfg.extracto_dos_columnas
                    ? [
                        ['col_debito_extracto', 'Col. débito (salidas) extracto', true],
                        ['col_credito_extracto', 'Col. crédito (entradas) extracto', true],
                      ]
                    : [['col_valor_extracto', 'Col. valor extracto', true]]),
                  ['col_concepto_extracto', 'Col. concepto extracto', true],
                  ['col_fecha_auxiliar', 'Col. fecha auxiliar', false],
                  ['col_debito_auxiliar', 'Col. débito auxiliar', false],
                  ['col_credito_auxiliar', 'Col. crédito auxiliar', false],
                  ['tolerancia_dias', 'Tolerancia días', false],
                ]
                  .filter(([, , soloExcel]) => !(extractoEsPDF && soloExcel))
                  .map(([k, lbl]) => (
                    <div key={k} style={s.field}>
                      <label style={s.label}>{lbl}</label>
                      <input style={s.input} name={k} value={cfg[k]} onChange={handle} />
                    </div>
                  ))}
              </div>
            </details>

            {error && <p style={s.error}>{error}</p>}

            <button style={{ ...s.btn, opacity: loading ? 0.6 : 1 }} onClick={conciliar} disabled={loading}>
              {loading ? '⏳ Procesando...' : '⚡ Conciliar'}
            </button>

            {resultado && (
              <>
                <button style={s.btnOutline} onClick={exportar} disabled={exporting}>
                  {exporting ? 'Exportando...' : '📥 Descargar Excel con colores'}
                </button>
                {!esDigitador && (
                  <button style={{ ...s.btnOutline, borderColor: '#16a34a', color: '#16a34a' }} onClick={generarReporte} disabled={generando}>
                    {generando ? 'Generando...' : '📄 Generar Reporte Formal'}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Panel derecho */}
          <div style={s.results}>
            {!resultado ? (
              <div style={s.empty}>
                <div style={{ fontSize: 48 }}>📊</div>
                <p>Sube los archivos y presiona Conciliar</p>
              </div>
            ) : (
              <>
                {/* Estadísticas */}
                <div style={s.stats}>
                  {[
                    ['Banco', resultado.total_banco, '#3b82f6'],
                    ['Auxiliar', resultado.total_aux, '#8b5cf6'],
                    ['Cruzan ✓', resultado.cruzan, '#16a34a'],
                    ['No cruzan ✗', resultado.no_cruzan_banco + resultado.no_cruzan_aux, '#dc2626'],
                  ].map(([lbl, val, color]) => (
                    <div key={lbl} style={s.stat}>
                      <div style={{ ...s.statVal, color }}>{val}</div>
                      <div style={s.statLbl}>{lbl}</div>
                    </div>
                  ))}
                </div>

                {resultado.porcentaje_conciliacion !== undefined && (
                  <div style={s.pctBar}>
                    <div style={{ ...s.pctFill, width: `${resultado.porcentaje_conciliacion}%` }} />
                    <span style={s.pctText}>{resultado.porcentaje_conciliacion}% conciliado</span>
                  </div>
                )}

                {/* Resumen de gastos bancarios (totalizado por categoría, ej. 4x1000, comisiones) */}
                {resultado.resumen_gastos && Object.keys(resultado.resumen_gastos).length > 0 && (
                  <div style={s.resumenGastos}>
                    <div style={s.resumenGastosTitle}>
                      💸 Gastos bancarios — total ${fmt(resultado.total_gastos_bancarios || 0)}
                    </div>
                    <div style={s.resumenGastosGrid}>
                      {Object.entries(resultado.resumen_gastos).map(([cat, g]) => (
                        <div key={cat} style={s.resumenGastosItem}>
                          <span style={s.resumenGastosCat}>{cat} <span style={s.resumenGastosCant}>({g.cantidad})</span></span>
                          <span style={s.resumenGastosVal}>${fmt(g.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Botón cruce manual */}
                {(resultado.no_cruzan_banco + resultado.no_cruzan_aux) > 0 && !esDigitador && (
                  <div style={{ marginBottom: 10 }}>
                    <button
                      style={{ ...s.btnCreuce, ...(modoCreuce ? s.btnCreuceActive : {}) }}
                      onClick={() => { setModoCreuce(v => !v); setSelBancoIdxs([]); setSelAuxIdxs([]); }}
                    >
                      ✏️ Cruce manual {crucesManuales.length > 0 && `(${crucesManuales.length} cruzado${crucesManuales.length > 1 ? 's' : ''})`}
                    </button>
                  </div>
                )}

                {/* ── MODO CRUCE MANUAL ── */}
                {modoCreuce ? (
                  <div>
                    <p style={s.cruceInstruccion}>
                      Selecciona <b>uno o varios ítems del banco</b> y <b>uno o varios del auxiliar</b> para
                      cruzarlos manualmente (por ejemplo, dos de $500 en el auxiliar contra un $1.000 del extracto).
                      {selBancoIdxs.length > 0 && selAuxIdxs.length > 0 && (
                        <span style={s.cruceTotales}>
                          Banco: <b>${fmt(totalSeleccionBanco())}</b> · Auxiliar: <b>${fmt(totalSeleccionAux())}</b>
                          {Math.round(totalSeleccionBanco() * 100) === Math.round(totalSeleccionAux() * 100)
                            ? <span style={{ color: '#16a34a', fontWeight: 700 }}> ✓ cuadran</span>
                            : <span style={{ color: '#d97706', fontWeight: 700 }}> ⚠ no cuadran</span>}
                        </span>
                      )}
                      {selBancoIdxs.length > 0 && selAuxIdxs.length > 0 &&
                        <button style={s.btnCruzarAhora} onClick={cruzarManual}>
                          ✅ Cruzar {selBancoIdxs.length > 1 || selAuxIdxs.length > 1 ? 'seleccionados' : 'estos dos'}
                        </button>}
                    </p>
                    <div style={s.cruceCols}>
                      {/* Columna Banco */}
                      <div style={s.cruceCol}>
                        <div style={s.cruceColTitle}>🏦 Banco — sin cruzar</div>
                        <div style={s.cruceScroll}>
                          {bancoPanelItems.map(item => item._isSection ? (
                            <span key={item._sectionKey} style={sHdr}>{item.label}</span>
                          ) : item._isGroup ? (
                            <div key={item._groupKey}
                              style={{ ...s.cruceItem, ...(selBancoIdxs.includes(item._groupKey) ? s.cruceItemSel : {}),
                                background: selBancoIdxs.includes(item._groupKey) ? '#fef3c7' : '#fffbeb',
                                borderLeft: '3px solid #f59e0b' }}
                              onClick={() => toggleSelBanco(item._groupKey)}>
                              <span style={s.cruceFecha}>{item._groupCount} cobro{item._groupCount !== 1 ? 's' : ''}</span>
                              <span style={{ ...s.cruceValor, color: '#92400e', fontWeight: 700 }}>${fmt(item._groupTotal)}</span>
                              <span style={s.cruceDesc}>{item._groupCat}</span>
                            </div>
                          ) : (
                            <div key={`b${item._origIdx}`}
                              style={{ ...s.cruceItem, ...(selBancoIdxs.includes(item._origIdx) ? s.cruceItemSel : {}) }}
                              onClick={() => toggleSelBanco(item._origIdx)}>
                              <span style={s.cruceFecha}>{item['FECHA'] || ''}</span>
                              <span style={s.cruceValor}>{item['VALOR'] || ''}</span>
                              <span style={s.cruceDesc}>{(item['DETALLE'] || '').toString().substring(0, 45)}</span>
                            </div>
                          ))}
                          {bancoPanelItems.length === 0 &&
                            <div style={s.cruceVacio}>Sin ítems pendientes</div>}
                        </div>
                      </div>
                      {/* Columna Auxiliar + Partidas (ordenada como el reporte) */}
                      <div style={s.cruceCol}>
                        <div style={s.cruceColTitle}>📒 Auxiliar / Partidas — sin cruzar</div>
                        <div style={s.cruceScroll}>
                          {/* Consig. no en extracto */}
                          {auxSorted.consig.length > 0 && (
                            <>
                              <span style={sHdr}>{TIPO_LABELS.consig_no_extracto}</span>
                              {auxSorted.consig.map(({ r, i }) => (
                                <div key={i}
                                  style={{ ...s.cruceItem, ...(selAuxIdxs.includes(i) ? s.cruceItemSel : {}) }}
                                  onClick={() => toggleSelAux(i)}>
                                  <span style={s.cruceFecha}>{auxGetFecha(r, resultado.headers_aux, cfg)}</span>
                                  <span style={{ ...s.cruceValor, color: '#15803d', fontWeight: 600 }}>{auxGetVal(r, resultado.headers_aux, cfg)}</span>
                                  <span style={s.cruceDesc}>{auxGetDesc(r, resultado.headers_aux, cfg)}</span>
                                </div>
                              ))}
                            </>
                          )}
                          {/* Cheques / Créditos pendientes */}
                          {auxSorted.cheques.length > 0 && (
                            <>
                              <span style={sHdr}>{TIPO_LABELS.cheques_pendientes}</span>
                              {auxSorted.cheques.map(({ r, i }) => (
                                <div key={i}
                                  style={{ ...s.cruceItem, ...(selAuxIdxs.includes(i) ? s.cruceItemSel : {}) }}
                                  onClick={() => toggleSelAux(i)}>
                                  <span style={s.cruceFecha}>{auxGetFecha(r, resultado.headers_aux, cfg)}</span>
                                  <span style={{ ...s.cruceValor, color: '#15803d', fontWeight: 600 }}>{auxGetVal(r, resultado.headers_aux, cfg)}</span>
                                  <span style={s.cruceDesc}>{auxGetDesc(r, resultado.headers_aux, cfg)}</span>
                                </div>
                              ))}
                            </>
                          )}
                          {/* Partidas manuales agrupadas por tipo */}
                          {partidas.length > 0 && TIPO_ORDER.flatMap(tipo => {
                            const tipoItems = partidas.map((p, origIdx) => ({ p, origIdx })).filter(({ p }) => p.tipo === tipo);
                            if (tipoItems.length === 0) return [];
                            return [
                              <span key={`shdr_p_${tipo}`} style={{ ...sHdr, background: '#166534', marginTop: 6 }}>{TIPO_LABELS[tipo]} (partida)</span>,
                              ...tipoItems.map(({ p, origIdx }) => (
                                <div key={`part_${origIdx}`}
                                  style={{ ...s.cruceItem, ...(selAuxIdxs.includes(`part_${origIdx}`) ? s.cruceItemSel : {}),
                                    background: selAuxIdxs.includes(`part_${origIdx}`) ? '#dcfce7' : '#f0fdf4',
                                    borderLeft: '3px solid #16a34a' }}
                                  onClick={() => toggleSelAux(`part_${origIdx}`)}>
                                  <span style={s.cruceFecha}>{p.fecha || ''}</span>
                                  <span style={{ ...s.cruceValor, color: '#15803d', fontWeight: 700 }}>${fmt(p.valor)}</span>
                                  <span style={s.cruceDesc}>{p.descripcion}</span>
                                </div>
                              ))
                            ];
                          })}
                          {auxSorted.consig.length === 0 && auxSorted.cheques.length === 0 && partidas.length === 0 &&
                            <div style={s.cruceVacio}>Sin ítems pendientes</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ── VISTA NORMAL ── */
                  <>
                    <div style={s.tabs}>
                      {[['banco', 'Banco'], ['auxiliar', 'Auxiliar'], ['diff', 'Solo diferencias']].map(([k, lbl]) => (
                        <button key={k} style={{ ...s.tabBtn, ...(tab === k ? s.tabActive : {}) }} onClick={() => setTab(k)}>
                          {lbl}
                          {k === 'diff' && <span style={s.badge}>{resultado.no_cruzan_banco + resultado.no_cruzan_aux}</span>}
                        </button>
                      ))}
                    </div>

                    <div style={s.tableWrap}>
                      <table style={s.table}>
                        <thead>
                          <tr>
                            {(tab === 'auxiliar' ? resultado.headers_aux : resultado.headers).filter(h => h && h !== 'nan').map(h => (
                              <th key={h} style={s.th}>{h}</th>
                            ))}
                            <th style={s.th}>Cruza</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, i) => (
                            <tr key={i} style={row._cruza ? {} : s.rowNo}>
                              {(tab === 'auxiliar' ? resultado.headers_aux : resultado.headers).filter(h => h && h !== 'nan').map(h => (
                                <td key={h} style={s.td}>{row[h]}</td>
                              ))}
                              <td style={s.td}>
                                <span style={row._cruza ? s.badgeSi : s.badgeNo}>
                                  {row._cruza ? 'Sí' : 'No'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DropZone({ label, icon, file, onChange, accept = '.xlsx,.xls' }) {
  const ref = useRef();
  const [drag, setDrag] = useState(false);
  return (
    <div style={{ ...dz.zone, ...(file ? dz.ready : {}), ...(drag ? dz.drag : {}) }}
      onClick={() => ref.current.click()}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); onChange(e.dataTransfer.files[0]); }}>
      <input ref={ref} type="file" accept={accept} style={{ display: 'none' }} onChange={e => onChange(e.target.files[0])} />
      <span style={{ fontSize: 20 }}>{file ? '✅' : icon}</span>
      <span style={dz.lbl}>{file ? file.name : label}</span>
      <span style={dz.sub}>{file ? 'Cargado' : accept.includes('pdf') ? 'Arrastra o haz clic · Excel o PDF' : 'Arrastra o haz clic'}</span>
    </div>
  );
}

const dz = {
  zone: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', border: '1.5px dashed #d1d5db', borderRadius: 10, cursor: 'pointer', marginBottom: 10, background: '#fafafa' },
  ready: { borderColor: '#16a34a', background: '#f0fdf4', borderStyle: 'solid' },
  drag: { borderColor: '#3b82f6', background: '#eff6ff' },
  lbl: { fontSize: 13, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sub: { fontSize: 11, color: '#888' }
};

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui,sans-serif' },
  nav: { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navBrand: { fontWeight: 700, fontSize: 16 },
  navRight: { display: 'flex', alignItems: 'center', gap: 12 },
  navUser: { fontSize: 13, color: '#555' },
  navLink: { fontSize: 13, color: '#2563eb', textDecoration: 'none' },
  navBtn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'transparent', cursor: 'pointer', fontSize: 13 },
  content: { padding: '24px', maxWidth: 1400, margin: '0 auto' },
  grid: { display: 'grid', gridTemplateColumns: '380px 1fr', gap: 20 },
  panel: { background: '#fff', borderRadius: 14, padding: '20px', border: '1px solid #e5e7eb', height: 'fit-content' },
  panelTitle: { fontSize: 16, fontWeight: 600, marginBottom: 16, margin: '0 0 16px' },
  field: { marginBottom: 10 },
  label: { fontSize: 12, color: '#666', display: 'block', marginBottom: 4, fontWeight: 500 },
  input: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box' },
  details: { marginBottom: 14, border: '1px solid #e5e7eb', borderRadius: 10, padding: '8px 12px' },
  summary: { cursor: 'pointer', fontSize: 13, color: '#555', fontWeight: 500 },
  advGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 },
  error: { color: '#dc2626', fontSize: 13, margin: '8px 0' },
  btn: { width: '100%', padding: '12px', borderRadius: 10, background: '#2563eb', color: '#fff', border: 'none', fontWeight: 600, fontSize: 15, cursor: 'pointer', marginTop: 4 },
  btnOutline: { width: '100%', padding: '10px', borderRadius: 10, background: '#fff', color: '#2563eb', border: '1.5px solid #2563eb', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginTop: 8 },
  results: { background: '#fff', borderRadius: 14, padding: '20px', border: '1px solid #e5e7eb', minHeight: 400 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: '#888', gap: 12 },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 },
  stat: { background: '#f8fafc', borderRadius: 10, padding: '12px', textAlign: 'center', border: '1px solid #e5e7eb' },
  statVal: { fontSize: 24, fontWeight: 700 },
  statLbl: { fontSize: 12, color: '#666', marginTop: 2 },
  tabs: { display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid #e5e7eb', paddingBottom: 0 },
  tabBtn: { padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: '#666', borderBottom: '2px solid transparent', marginBottom: -1, display: 'flex', alignItems: 'center', gap: 6 },
  tabActive: { color: '#111', borderBottomColor: '#2563eb', fontWeight: 600 },
  badge: { background: '#fee2e2', color: '#dc2626', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 600 },
  tableWrap: { overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { background: '#f8fafc', padding: '8px 12px', textAlign: 'left', fontWeight: 500, color: '#555', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' },
  td: { padding: '6px 12px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' },
  rowNo: { background: '#fff5f5' },
  badgeSi: { background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  badgeNo: { background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  saldosBox: { background: '#f8fafc', borderRadius: 10, padding: '12px', marginBottom: 14, border: '1px solid #e5e7eb' },
  saldosTitle: { fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#333' },
  sumasBox: { marginTop: 10, padding: '8px', background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' },
  sumaRow: { display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: '#444' },
  sumaVal: { fontWeight: 600 },
  pctBar: { position: 'relative', height: 24, background: '#f0f0f0', borderRadius: 12, marginBottom: 16, overflow: 'hidden' },
  pctFill: { position: 'absolute', left: 0, top: 0, height: '100%', background: '#16a34a', borderRadius: 12, transition: 'width 0.5s' },
  pctText: { position: 'absolute', width: '100%', textAlign: 'center', lineHeight: '24px', fontSize: 12, fontWeight: 600, color: '#fff', mixBlendMode: 'difference' },
  resumenGastos: { background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '10px 14px', marginBottom: 12 },
  resumenGastosTitle: { fontSize: 13, fontWeight: 700, color: '#9a3412', marginBottom: 8 },
  resumenGastosGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 6 },
  resumenGastosItem: { display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, background: '#fff', borderRadius: 6, padding: '5px 9px', border: '1px solid #ffedd5' },
  resumenGastosCat: { color: '#7c2d12', fontWeight: 600 },
  resumenGastosCant: { color: '#c2410c', fontWeight: 400 },
  resumenGastosVal: { color: '#7c2d12', fontWeight: 700 },
  partidasList: { marginBottom: 10 },
  partidaItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', background: '#f8fafc', borderRadius: 8, marginBottom: 4, border: '1px solid #e5e7eb' },
  partidaInfo: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 },
  partidaTipo: { fontSize: 10, color: '#2563eb', fontWeight: 600 },
  partidaDesc: { fontSize: 12, color: '#333' },
  partidaVal: { fontSize: 12, fontWeight: 600, color: '#16a34a' },
  btnDel: { background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14, padding: '0 4px' },
  nuevaPartida: { display: 'flex', flexDirection: 'column', gap: 6 },
  btnAgregar: { padding: '8px', borderRadius: 8, background: '#2563eb', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: 13 },
  pdfNote: { fontSize: 12, color: '#6d28d9', background: '#f5f3ff', padding: '6px 10px', borderRadius: 8, marginBottom: 8, border: '1px solid #ddd6fe' },
  pdfConvertLink: { display: 'inline', padding: 0, background: 'none', border: 'none', color: '#6d28d9', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontSize: 12 },
  pdfNoteSmall: { fontSize: 11, color: '#6d28d9', margin: '6px 0 0', padding: '4px 8px', background: '#f5f3ff', borderRadius: 6 },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#444', margin: '8px 0 4px', cursor: 'pointer' },
  btnManage: { padding: '8px 12px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e5e7eb', cursor: 'pointer', fontSize: 16, textDecoration: 'none', display: 'flex', alignItems: 'center' },
  cuentaEmpty: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: 10, fontSize: 12, color: '#888' },
  btnSaveTpl: { padding: '8px 12px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', cursor: 'pointer', fontSize: 16 },
  btnDelTpl: { marginTop: 4, fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  tplSaveBox: { background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', marginBottom: 10 },
  rolNote: { fontSize: 12, color: '#92400e', background: '#fef3c7', padding: '7px 10px', borderRadius: 8, marginBottom: 10, border: '1px solid #fde68a' },
  trasladoBox: { marginTop: 14, padding: '10px 12px', background: '#f0f9ff', borderRadius: 10, border: '1px solid #bae6fd' },
  trasladoTitle: { fontSize: 12, fontWeight: 600, color: '#0369a1', marginBottom: 6 },
  trasladoWarn: { fontSize: 11, color: '#92400e', margin: '4px 0 0', padding: '4px 8px', background: '#fef3c7', borderRadius: 6 },
  btnGuardarPartidas: { marginTop: 10, width: '100%', padding: '8px', borderRadius: 8, background: '#f0fdf4', color: '#16a34a', border: '1.5px solid #86efac', fontWeight: 600, fontSize: 12, cursor: 'pointer' },
  btnCreuce: { padding: '7px 16px', borderRadius: 8, background: '#f8fafc', border: '1.5px solid #e5e7eb', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#444' },
  btnCreuceActive: { background: '#eff6ff', borderColor: '#3b82f6', color: '#2563eb' },
  cruceInstruccion: { fontSize: 12, color: '#555', marginBottom: 10, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  cruceTotales: { fontSize: 12, color: '#374151', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '4px 10px' },
  btnCruzarAhora: { padding: '6px 14px', borderRadius: 8, background: '#16a34a', color: '#fff', border: 'none', fontWeight: 600, fontSize: 12, cursor: 'pointer' },
  cruceCols: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  cruceCol: { border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' },
  cruceColTitle: { background: '#f8fafc', padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#555', borderBottom: '1px solid #e5e7eb' },
  cruceScroll: { maxHeight: 380, overflowY: 'auto' },
  cruceItem: { padding: '7px 10px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 },
  cruceItemSel: { background: '#eff6ff', borderLeft: '3px solid #3b82f6' },
  cruceFecha: { color: '#888', fontSize: 10 },
  cruceValor: { fontWeight: 700, color: '#1e40af' },
  cruceDesc: { color: '#333' },
  cruceVacio: { padding: '16px', textAlign: 'center', color: '#aaa', fontSize: 12 },
};
